/**
 * Pure helpers to go from the raw `/api/v1/grid/:ugm/:gridId` payload to the
 * board-facing `SeatData[]` the `<TakeoverGrid />` canvas renders.
 *
 * Structurally matches the main app's `use-grid-page.helpers.ts#buildSeatData`
 * but drops the lobby-only knobs (rewards breakdown, priority holders, anti-
 * snipe waterfall overlays, creator-identity merge). A builder embedding the
 * grid should see the same seat state the app shows, minus the lobby-only
 * pool-aware overlays.
 */

import { V2_CONSTANTS, type SeatData, type V2GridPageData, type V2GridSeat } from './types';

interface BuildSeatDataOptions {
  /** Viewer's wallet address — used to flag `isOwn`. */
  viewerAddress?: string | null;
  /** Viewer's display name override (for own seats in hover state). */
  viewerDisplayName?: string | null;
  viewerAvatarUrl?: string | null;
  viewerLevel?: number;
}

/**
 * Empty seat placeholder used when the API returns fewer seats than the
 * grid capacity (e.g. just-graduated grid with 0 sold yet).
 */
export function createEmptySeat(seatIndex: number): SeatData {
  return {
    seatIndex,
    holder: null,
    selfAssessedPrice: 0n,
    balance: 0n,
    runwaySeconds: 0,
  };
}

function upgradeAvatarUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  if (url.includes('pbs.twimg.com') || url.includes('twimg.com')) {
    return url
      .replace(/_normal\./, '_400x400.')
      .replace(/_bigger\./, '_400x400.')
      .replace(/_200x200\./, '_400x400.')
      .replace(/_mini\./, '_400x400.');
  }
  return url;
}

function calcRunwayFromDeposit(
  depositWei: bigint,
  priceWei: bigint,
  taxRateBps: bigint,
): number {
  if (priceWei <= 0n || taxRateBps <= 0n) return 0;
  const weekSeconds = BigInt(V2_CONSTANTS.SECONDS_PER_WEEK);
  const denom = BigInt(V2_CONSTANTS.TAX_RATE_DENOMINATOR);
  const weeklyTax = (priceWei * taxRateBps) / denom;
  if (weeklyTax <= 0n) return 0;
  return Number((depositWei * weekSeconds) / weeklyTax);
}

/**
 * Transform the raw V2 grid payload into the `SeatData[]` shape the canvas
 * consumes. Fills missing indices with `createEmptySeat` so the caller gets
 * exactly `grid.totalSeats` entries.
 */
export function buildSeatData(
  page: V2GridPageData,
  options: BuildSeatDataOptions = {},
): SeatData[] {
  const { grid, seats: rawSeats, reservations, claimDeadline } = page;
  const {
    viewerAddress,
    viewerDisplayName,
    viewerAvatarUrl,
    viewerLevel,
  } = options;
  const viewerLower = viewerAddress ? viewerAddress.toLowerCase() : null;
  const creatorLower = grid.creator.toLowerCase();
  const taxRate = BigInt(grid.taxRateBps || 0);
  const now = Math.floor(Date.now() / 1000);

  const reservationMap = new Map<
    number,
    {
      claimant: string;
      claimantIdentity?: { displayName?: string; avatarUrl?: string; level?: number };
    }
  >();
  if (reservations && claimDeadline && claimDeadline > now) {
    for (const r of reservations) reservationMap.set(r.seatIndex, r);
  }

  const seatByIndex = new Map<number, SeatData>();
  for (const seat of rawSeats) {
    const price = BigInt(seat.selfAssessedPrice);
    const deposit = BigInt(seat.deposit);

    const isCreatorSeat =
      seat.holder?.toLowerCase() === creatorLower &&
      deposit === 0n &&
      !grid.graduated;

    let currentDeposit: bigint;
    let runwaySeconds: number;
    const hasRpcBalance = typeof seat.rpcCurrentBalance === 'string';
    if (hasRpcBalance && !isCreatorSeat) {
      const rpcBal = BigInt(seat.rpcCurrentBalance!);
      currentDeposit = rpcBal > 0n ? rpcBal : 0n;
      runwaySeconds = calcRunwayFromDeposit(currentDeposit, price, taxRate);
    } else {
      const weekSeconds = BigInt(V2_CONSTANTS.SECONDS_PER_WEEK);
      const denom = BigInt(V2_CONSTANTS.TAX_RATE_DENOMINATOR);
      const weeklyTax = isCreatorSeat ? 0n : (price * taxRate) / denom;
      const lastTax = seat.lastTaxTime || seat.heldSince || seat.createdAt || now;
      const elapsed = BigInt(Math.max(0, now - lastTax));
      const pendingTax = weeklyTax > 0n ? (weeklyTax * elapsed) / weekSeconds : 0n;
      currentDeposit = deposit > pendingTax ? deposit - pendingTax : 0n;
      runwaySeconds = weeklyTax > 0n ? Number((currentDeposit * weekSeconds) / weeklyTax) : 0;
    }

    const isOwn = !!viewerLower && seat.holder?.toLowerCase() === viewerLower;
    const reservation = reservationMap.get(seat.seatIndex);
    const isReserved = !!reservation && !seat.holder;
    const isOwnReservation =
      isReserved && !!viewerLower && reservation!.claimant.toLowerCase() === viewerLower;

    const projected: SeatData = {
      seatIndex: seat.seatIndex,
      holder: seat.holder ?? null,
      holderName:
        isOwn && viewerDisplayName
          ? viewerDisplayName
          : (seat.holderIdentity?.displayName ?? null),
      holderAvatar: upgradeAvatarUrl(
        isOwn && viewerAvatarUrl ? viewerAvatarUrl : seat.holderIdentity?.avatarUrl,
      ),
      holderLevel: isOwn && viewerLevel !== undefined ? viewerLevel : seat.holderIdentity?.level,
      selfAssessedPrice: price,
      balance: currentDeposit,
      runwaySeconds,
      claimedAt: seat.heldSince || seat.createdAt || 0,
      lastUpdateTime: seat.lastTaxTime || seat.heldSince || seat.createdAt || now,
      lastPriceChangeTime: seat.lastPriceChangeTime,
      createdAt: seat.createdAt,
      isOwn: isOwn || isOwnReservation,
      isCreatorSeat,
      isForfeited:
        !isCreatorSeat &&
        seat.isActive &&
        seat.holder !== null &&
        currentDeposit <= 0n &&
        hasRpcBalance,
      isPendingForfeit:
        !isCreatorSeat &&
        seat.holder !== null &&
        currentDeposit <= 0n &&
        !hasRpcBalance,
      taxRateBps: taxRate,
      ...(isReserved
        ? {
            isReserved: true,
            reservedFor: reservation!.claimant,
            claimDeadline,
            reservedForName: reservation!.claimantIdentity?.displayName,
            reservedForAvatar: upgradeAvatarUrl(reservation!.claimantIdentity?.avatarUrl),
          }
        : {}),
    };
    seatByIndex.set(seat.seatIndex, projected);
  }

  const total = grid.totalSeats || V2_CONSTANTS.TOTAL_SEATS;
  const out: SeatData[] = new Array(total);
  for (let i = 0; i < total; i++) {
    out[i] = seatByIndex.get(i) ?? createEmptySeat(i);
  }
  return out;
}

/**
 * Merge a partial live-seats update (from SSE) into a baseline `SeatData[]`
 * produced by `buildSeatData`. Returns a new array only when something
 * changed so React can skip re-renders.
 */
export interface LiveSeatUpdate {
  seatIndex: number;
  holder: string | null;
  selfAssessedPrice: string;
  balance: string;
  lastUpdateTime: number;
  isActive: boolean;
}

export function mergeLiveSeats(
  baseline: SeatData[],
  updates: LiveSeatUpdate[],
  taxRateBps: bigint,
): SeatData[] {
  if (!updates.length) return baseline;
  let changed = false;
  const next = baseline.slice();
  for (const u of updates) {
    const prev = next[u.seatIndex];
    if (!prev) continue;
    const price = BigInt(u.selfAssessedPrice);
    const balance = BigInt(u.balance);
    if (
      prev.holder === u.holder &&
      prev.selfAssessedPrice === price &&
      prev.balance === balance &&
      prev.lastUpdateTime === u.lastUpdateTime
    ) {
      continue;
    }
    const runwaySeconds = calcRunwayFromDeposit(balance, price, taxRateBps);
    next[u.seatIndex] = {
      ...prev,
      holder: u.holder,
      selfAssessedPrice: price,
      balance,
      runwaySeconds,
      lastUpdateTime: u.lastUpdateTime,
      isForfeited:
        !prev.isCreatorSeat && u.holder !== null && balance <= 0n,
    };
    changed = true;
  }
  return changed ? next : baseline;
}
